import React, { RefObject } from "react";
import { Box, Typography } from "@mui/material";
import Logo from "../../shared/img/logoframe.png";
import Background from "../../shared/img/abstractbackground.webp";

interface ShareTemplateProps {
  divRef: RefObject<HTMLDivElement>;
  children: React.ReactNode;
  cardTitle: string;
}

export default function ShareTemplate({ divRef, children, cardTitle }: ShareTemplateProps) {
  const iphoneWidth = 1170;
  const iphoneHeight = 2340;
  const cardWidth = 1050; // Keep horizontal size fixed

  return (
    <Box
      ref={divRef}
      sx={{
        position: "absolute",
        left: "-9999px",
        top: 0,
        width: `${iphoneWidth}px`,
        height: `${iphoneHeight}px`,
        backgroundImage: `url(${Background})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "space-between",
        overflow: "hidden",
      }}
    >
      {/* Upper third — logo (scales down when card content is tall) */}
      <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden", minHeight: 0, p: 2 }}>
        <Box
          component="img"
          src={Logo}
          alt="rank5.io"
          sx={{ maxWidth: "850px", width: "100%", maxHeight: "100%", objectFit: "contain" }}
        />
      </Box>

      {/* Card content — fixed width, auto height (fit vertically) */}
      <Box sx={{ width: `${cardWidth}px`, flexShrink: 0 }}>{children}</Box>

      {/* Lower third — brand */}
      <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", px: 4 }}>
        <Typography sx={{ color: "rgba(255,255,255,0.9)", fontSize: "64px", letterSpacing: "0.01em", fontWeight: 600, textAlign: "center", lineHeight: 1.2 }}>
          find out what your friends think of you on{' '}
          <Box component="span" sx={{ color: "primary.main", fontWeight: 800 }}>
            rank5.io
          </Box>
        </Typography>
      </Box>
    </Box>
  );
}

export async function shareCard(divRef: RefObject<HTMLDivElement>, cardTitle: string) {
  if (!divRef.current) return;

  try {
    const html2canvas = (await import("html2canvas")).default;
    const canvas = await html2canvas(divRef.current, { useCORS: true, scale: 2 });

    canvas.toBlob(async (blob) => {
      if (!blob) return;
      const file = new File([blob], `${cardTitle.replace(/\s+/g, "_")}.png`, {
        type: "image/png",
      });

      if (navigator.canShare?.({ files: [file] })) {
        try {
          await navigator.share({ files: [file], title: cardTitle });
          return;
        } catch {
          // fall through to download
        }
      }

      // Fallback: download
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${cardTitle.replace(/\s+/g, "_")}.png`;
      a.click();
      URL.revokeObjectURL(url);
    }, "image/png");
  } catch (err) {
    console.error("share failed", err);
  }
}
